import { LogLevel, Configuration, PopupRequest } from '@azure/msal-browser';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: process.env.NEXT_PUBLIC_AZURE_AD_CLIENT_ID!,
    authority: process.env.NEXT_PUBLIC_AZURE_AD_AUTHORITY!,
    redirectUri: process.env.NEXT_PUBLIC_AZURE_AD_REDIRECT_URI!,
    postLogoutRedirectUri:
      typeof window !== 'undefined'
        ? window.location.origin + '/sign_in'
        : 'http://localhost:3000/sign_in',
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        // Get existing logs array or create new one

        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            return;
          case LogLevel.Info:
            return;
          case LogLevel.Verbose:
            return;
          case LogLevel.Warning:
            return;
          default:
            return;
        }
      },
      logLevel: LogLevel.Info, // Set to LogLevel.Verbose for detailed logs
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: PopupRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
};

/**
 * Silent token request configuration
 * Used when acquiring tokens without user interaction
 */
export const silentRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
};

/**
 * Microsoft Graph API configuration
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  graphUsersEndpoint: 'https://graph.microsoft.com/v1.0/users',
};

/**
 * Logout request configuration
 */
export const logoutRequest = {
  postLogoutRedirectUri:
    typeof window !== 'undefined'
      ? window.location.origin + '/sign_in'
      : 'http://localhost:3000/sign_in',
};

/**
 * OAuth callback configuration constants
 */
export const oauthConfig = {
  redirectDelays: {
    success: 2000,
    error: 2000,
    noAccount: 1000,
    tokenFailed: 2000,
    authFailed: 2000,
  },
  errorParams: {
    tokenFailed: 'token_failed',
    oauthFailed: 'oauth_failed',
    authCheckFailed: 'auth_check_failed',
  },
  routes: {
    signIn: '/sign_in',
    home: '/home/',
  },
  messages: {
    noAccount: 'No account found. Please try again.',
    tokenFailed: 'Failed to acquire token. Redirecting...',
    authFailed: 'Authentication failed. Redirecting to sign in...',
    serverAuthFailed: 'Failed to authenticate with server',
    authVerificationFailed: 'Authentication verification failed',
    verificationFailed: 'Failed to verify authentication',
    completingSignIn: 'Completing sign in...',
  },
} as const;
